import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Box, Button, HStack, Text } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
const shakeAndVibrate = keyframes `
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  5% { transform: translate(-5px, -3px) rotate(-3deg); }
  10% { transform: translate(5px, 3px) rotate(3deg); }
  15% { transform: translate(-4px, -2px) rotate(-2deg); }
  20% { transform: translate(4px, 2px) rotate(2deg); }
  25% { transform: translate(-3px, -3px) rotate(-2deg); }
  30% { transform: translate(3px, 3px) rotate(2deg); }
  35% { transform: translate(-2px, -2px) rotate(-1deg); }
  40% { transform: translate(2px, 2px) rotate(1deg); }
  45% { transform: translate(-2px, -1px) rotate(-1deg); }
  50% { transform: translate(2px, 1px) rotate(1deg); }
  55% { transform: translate(-1px, -2px) rotate(-0.5deg); }
  60% { transform: translate(1px, 2px) rotate(0.5deg); }
  65% { transform: translate(-1px, -1px) rotate(-0.5deg); }
  70% { transform: translate(1px, 1px) rotate(0.5deg); }
  75% { transform: translate(-1px, 0) rotate(-0.5deg); }
  80% { transform: translate(1px, 0) rotate(0.5deg); }
  85% { transform: translate(-0.5px, -0.5px) rotate(-0.25deg); }
  90% { transform: translate(0.5px, 0.5px) rotate(0.25deg); }
  95% { transform: translate(0, 0) rotate(0deg); }
`;
const highlightGlow = keyframes `
  0%, 100% { 
    box-shadow: 0 0 0px rgba(129, 85, 254, 0);
    border-color: #4E4E4E;
    background-color: transparent;
  }
  10% { 
    box-shadow: 0 0 15px rgba(129, 85, 254, 0.8), 0 0 25px rgba(129, 85, 254, 0.6);
    border-color: #8155FE;
    background-color: rgba(129, 85, 254, 0.2);
  }
  20% { 
    box-shadow: 0 0 20px rgba(129, 85, 254, 0.9), 0 0 30px rgba(129, 85, 254, 0.7);
    border-color: #9D7AFF;
    background-color: rgba(129, 85, 254, 0.3);
  }
  30% { 
    box-shadow: 0 0 18px rgba(129, 85, 254, 0.85), 0 0 28px rgba(129, 85, 254, 0.65);
    border-color: #8155FE;
    background-color: rgba(129, 85, 254, 0.25);
  }
  40% { 
    box-shadow: 0 0 15px rgba(129, 85, 254, 0.8), 0 0 25px rgba(129, 85, 254, 0.6);
    border-color: #9D7AFF;
    background-color: rgba(129, 85, 254, 0.2);
  }
  50% { 
    box-shadow: 0 0 20px rgba(129, 85, 254, 0.9), 0 0 30px rgba(129, 85, 254, 0.7);
    border-color: #8155FE;
    background-color: rgba(129, 85, 254, 0.3);
  }
  60% { 
    box-shadow: 0 0 18px rgba(129, 85, 254, 0.85), 0 0 28px rgba(129, 85, 254, 0.65);
    border-color: #9D7AFF;
    background-color: rgba(129, 85, 254, 0.25);
  }
  70% { 
    box-shadow: 0 0 12px rgba(129, 85, 254, 0.7), 0 0 20px rgba(129, 85, 254, 0.5);
    border-color: #8155FE;
    background-color: rgba(129, 85, 254, 0.15);
  }
  80% { 
    box-shadow: 0 0 8px rgba(129, 85, 254, 0.5), 0 0 15px rgba(129, 85, 254, 0.3);
    border-color: #8155FE;
    background-color: rgba(129, 85, 254, 0.1);
  }
  90% { 
    box-shadow: 0 0 4px rgba(129, 85, 254, 0.3), 0 0 8px rgba(129, 85, 254, 0.2);
    border-color: #6B5A8E;
    background-color: rgba(129, 85, 254, 0.05);
  }
`;
const PassCounterTable = ({ label, initialValue = 0, onChange, shouldShake = false, }) => {
    const [count, setCount] = useState(initialValue);
    const [isAnimating, setIsAnimating] = useState(false);
    const handleIncrement = () => setCount(count + 1);
    const handleDecrement = () => setCount(count > 0 ? count - 1 : 0);
    useEffect(() => {
        onChange(count);
    }, [count, onChange]);
    useEffect(() => {
        if (shouldShake) {
            setIsAnimating(true);
            // Trigger haptic vibration if supported (mobile devices)
            if (navigator.vibrate) {
                // Vibrate pattern: 50ms on, 30ms off, 50ms on, 30ms off, 50ms on
                navigator.vibrate([50, 30, 50, 30, 50, 30, 50, 30, 50]);
            }
            const timer = setTimeout(() => setIsAnimating(false), 1000);
            return () => clearTimeout(timer);
        }
    }, [shouldShake]);
    return (_jsxs(Box, { display: "flex", alignItems: "center", justifyContent: "space-between", color: "white", w: "100%", borderBottom: "1px solid #2F2F2F", pb: 4, children: [_jsx(Text, { fontWeight: "400", fontSize: "12px", lineHeight: "20px", children: label }), _jsxs(HStack, { spacing: 2, children: [_jsx(Button, { onClick: handleDecrement, w: "38px", bg: "transparent", borderRadius: "full", border: "1px solid #4E4E4E", color: "white", _hover: { bg: "gray.600" }, children: "\u2013" }), _jsx(Text, { fontWeight: "700", px: 1, fontSize: "14px", color: "#FFFFFF", children: count }), _jsx(Button, { onClick: handleIncrement, w: "38px", bg: isAnimating ? "rgba(129, 85, 254, 0.2)" : "transparent", borderRadius: "full", border: isAnimating ? "1px solid #8155FE" : "1px solid #4E4E4E", color: "white", _hover: { bg: "gray.600" }, animation: isAnimating
                            ? `${shakeAndVibrate} 1s ease-in-out, ${highlightGlow} 1s ease-in-out`
                            : "none", transition: "all 0.2s ease-in-out", children: "+" })] })] }));
};
export default PassCounterTable;
